import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import { Plus, Search, Filter, Music, ChevronRight, Trash2, Edit3 } from 'lucide-react';
import toast from 'react-hot-toast';

const statusConfig = {
  draft: { label: { it: 'Bozza', en: 'Draft' }, color: 'status-draft' },
  in_review: { label: { it: 'In Revisione', en: 'In Review' }, color: 'status-in_review' },
  approved: { label: { it: 'Approvata', en: 'Approved' }, color: 'status-approved' },
  rejected: { label: { it: 'Rifiutata', en: 'Rejected' }, color: 'status-rejected' },
  distributed: { label: { it: 'Live', en: 'Live' }, color: 'status-distributed' },
  modification_requested: { label: { it: 'Modifica Richiesta', en: 'Modification Requested' }, color: 'status-modification_requested' },
  takedown_requested: { label: { it: 'Take Down', en: 'Takedown' }, color: 'status-takedown_requested' },
  removed: { label: { it: 'Rimossa', en: 'Removed' }, color: 'status-removed' },
};

const typeLabels = { single: 'Singolo', ep: 'EP', album: 'Album' };

export default function ReleasesPage() {
  const { user } = useAuth();
  const lang = user?.language || 'it';
  const [releases, setReleases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '', search: '', page: 1 });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { loadReleases(); }, [filters]);

  const loadReleases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page);
      params.append('limit', 20);
      const { data } = await api.get(`/releases?${params}`);
      setReleases(data.releases);
      setTotal(data.total);
    } catch (err) { toast.error('Errore caricamento release'); }
    finally { setLoading(false); }
  };

  const deleteRelease = async (id) => {
    if (!window.confirm(lang === 'it' ? 'Eliminare questa bozza?' : 'Delete this draft?')) return;
    try {
      await api.delete(`/releases/${id}`);
      toast.success(lang === 'it' ? 'Bozza eliminata' : 'Draft deleted');
      loadReleases();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-pn-dark">{lang === 'it' ? 'Le Mie Release' : 'My Releases'}</h1>
          <p className="text-sm text-gray-500 mt-1">{total} {lang === 'it' ? 'release totali' : 'total releases'}</p>
        </div>
        <Link to="/releases/new" className="btn-primary inline-flex items-center gap-2">
          <Plus size={18} /> {lang === 'it' ? 'Nuova Release' : 'New Release'}
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input-field pl-10" placeholder={lang === 'it' ? 'Cerca per titolo, UPC...' : 'Search by title, UPC...'}
              value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value, page: 1 }))} />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary inline-flex items-center gap-2">
            <Filter size={16} /> Filtri
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t">
            <select className="input-field" value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value, page: 1 }))}>
              <option value="">{lang === 'it' ? 'Tutti gli stati' : 'All statuses'}</option>
              {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label[lang]}</option>)}
            </select>
            <select className="input-field" value={filters.type} onChange={e => setFilters(p => ({ ...p, type: e.target.value, page: 1 }))}>
              <option value="">{lang === 'it' ? 'Tutti i tipi' : 'All types'}</option>
              <option value="single">Singolo</option>
              <option value="ep">EP</option>
              <option value="album">Album</option>
            </select>
          </div>
        )}
      </div>

      {/* Releases list */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div>
      ) : releases.length === 0 ? (
        <div className="card p-12 text-center">
          <Music size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">{lang === 'it' ? 'Nessuna release trovata' : 'No releases found'}</h3>
          <p className="text-sm text-gray-400 mb-4">{lang === 'it' ? 'Inizia creando la tua prima release!' : 'Start by creating your first release!'}</p>
          <Link to="/releases/new" className="btn-primary inline-flex items-center gap-2">
            <Plus size={18} /> {lang === 'it' ? 'Crea Release' : 'Create Release'}
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {releases.map(r => (
            <Link key={r.id} to={`/releases/${r.id}`} className="card p-4 flex items-center gap-4 hover:shadow-md transition group">
              <div className="w-14 h-14 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                {r.cover_art_path ? <img src={`/uploads/artwork/${r.user_id}/${r.cover_art_path}`} alt="" className="w-full h-full object-cover" />
                  : <Music size={20} className="m-auto mt-4 text-gray-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-pn-dark truncate">{r.title}</h3>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500 flex-shrink-0">{typeLabels[r.release_type]}</span>
                </div>
                <p className="text-sm text-gray-400 mt-0.5">{r.release_artist_names ? `${r.release_artist_names} · ` : ''}{r.label_name} · {r.track_count || 0} {lang === 'it' ? 'tracce' : 'tracks'} · {r.release_date ? r.release_date.split('T')[0] : '-'}</p>
              </div>
              <span className={`badge ${statusConfig[r.status]?.color || 'status-draft'} flex-shrink-0`}>
                {statusConfig[r.status]?.label[lang] || r.status}
              </span>
              {r.status === 'draft' && (
                <button onClick={(e) => { e.preventDefault(); deleteRelease(r.id); }} className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 size={16} />
                </button>
              )}
              <ChevronRight size={18} className="text-gray-300 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: Math.ceil(total / 20) }, (_, i) => (
            <button key={i} onClick={() => setFilters(p => ({ ...p, page: i + 1 }))}
              className={`w-9 h-9 rounded-lg text-sm font-medium ${filters.page === i + 1 ? 'bg-pn-red text-white' : 'bg-white border hover:bg-gray-50'}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
